import * as React from "react";
import {
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from "firebase/auth";
import { ArrowLeft, Mail, Phone, Sparkles, type LucideIcon } from "lucide-react";

import { FIREBASE_ENABLED } from "@/config";
import { getFirebaseAuth } from "@/lib/firebase";
import { usePortal } from "@/features/portal/portal-context";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PopinMark } from "@/components/popin-mark";

// ── Sign-in is data-driven ──────────────────────────────────────────────────
// Every method is one entry in AUTH_METHODS ({ id, label, icon, Form }). The
// switcher and the form region render straight from that list, so a future
// provider (Google, SSO, passkey…) is added by writing a Form component and
// pushing one object — no changes to the switcher or layout required. pop.in
// accounts authenticate against the shared Firebase project, exactly like the
// admin-gui; partner access itself is enforced by the portal context after the
// Firebase sign-in succeeds.
// ─────────────────────────────────────────────────────────────────────────────

/** Map a Firebase auth error to a short, human message. */
function friendlyAuthError(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code: unknown }).code)
      : "";
  switch (code) {
    case "auth/invalid-email":
      return "That doesn’t look like a valid email.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Incorrect email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/invalid-phone-number":
      return "Enter a valid phone number.";
    case "auth/invalid-verification-code":
    case "auth/code-expired":
      return "That code didn’t match. Request a new one and try again.";
    case "auth/captcha-check-failed":
      return "Verification failed. Please refresh and try again.";
    default:
      return "Something went wrong signing in. Please try again.";
  }
}

interface AuthFormProps {
  /** Clears any prior "no partner access" rejection before a fresh attempt. */
  onAttempt: () => void;
}

// ── Inline error / status note ──────────────────────────────────────────────
function FormNote({
  tone = "error",
  children,
}: {
  tone?: "error" | "info";
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "mt-3.5 rounded-[10px] border px-3 py-2.5 text-[13px] leading-[1.45]",
        tone === "error"
          ? "border-[rgba(255,90,90,.25)] bg-[rgba(255,90,90,.08)] text-[#ff8a8a]"
          : "border-border bg-panel-2 text-muted-foreground",
      )}
    >
      {children}
    </div>
  );
}

// ── Method switcher (renders straight from AUTH_METHODS) ─────────────────────
function MethodSwitcher({
  methods,
  value,
  onChange,
}: {
  methods: readonly AuthMethod[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Sign-in method"
      className="mb-6 grid gap-1 rounded-xl border border-border bg-field p-1"
      style={{ gridTemplateColumns: `repeat(${methods.length}, 1fr)` }}
    >
      {methods.map((m) => {
        const active = m.id === value;
        const Icon = m.icon;
        return (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(m.id)}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-[9px] border px-2.5 py-[9px] text-[13.5px] font-semibold transition-all",
              active
                ? "border-border bg-background text-foreground shadow-portal"
                : "border-transparent text-text-3",
            )}
          >
            <Icon className={cn("size-4", active ? "text-brand" : "text-text-3")} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Email + password ────────────────────────────────────────────────────────
function EmailForm({ onAttempt }: AuthFormProps) {
  const { authError } = usePortal();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [err, setErr] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  // A rejection from the context (signed in but no partner role) ends the
  // pending state so the button stops spinning.
  React.useEffect(() => {
    if (authError) setSubmitting(false);
  }, [authError]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (!email || !password) {
      setErr("Enter your email and password.");
      return;
    }
    if (!FIREBASE_ENABLED) {
      setErr("Authentication isn’t configured yet.");
      return;
    }
    onAttempt();
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
      // On success the portal context verifies partner access and redirects.
    } catch (error) {
      setSubmitting(false);
      setErr(friendlyAuthError(error));
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="flex flex-col gap-4">
        <div className="space-y-[7px]">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-[7px]">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>

      {err && <FormNote>{err}</FormNote>}

      <Button
        type="submit"
        size="lg"
        disabled={submitting}
        className="mt-[22px] w-full"
        style={submitting ? { opacity: 0.7, cursor: "wait" } : undefined}
      >
        {submitting ? "One moment…" : "Sign in"}
      </Button>
    </form>
  );
}

// ── One-time-code input (6 boxes, auto-advance + paste) ─────────────────────
function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
}: {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
}) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  function setAt(i: number, digit: string) {
    const next = value.split("");
    next[i] = digit;
    const joined = next.join("").slice(0, length);
    onChange(joined);
    return joined;
  }

  function handleChange(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(i, "");
      return;
    }
    const joined = setAt(i, raw[raw.length - 1]!);
    if (i < length - 1) refs.current[i + 1]?.focus();
    if (joined.length === length) onComplete?.(joined);
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !value[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!digits) return;
    onChange(digits);
    const target = Math.min(digits.length, length - 1);
    refs.current[target]?.focus();
    if (digits.length === length) onComplete?.(digits);
  }

  return (
    <div className="flex gap-2" onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`Digit ${i + 1}`}
          className="aspect-square w-full min-w-0 rounded-[11px] border border-border bg-field text-center font-mono text-[22px] font-bold text-foreground outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-[3px] focus:ring-ring"
        />
      ))}
    </div>
  );
}

/** Format mm:ss for the resend countdown. */
function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

// ── Phone number + SMS code (Firebase two-step) ─────────────────────────────
const PHONE_PREFIXES = ["+1", "+44", "+61", "+91", "+49", "+33", "+81"];
const RESEND_SECONDS = 120;

function PhoneForm({ onAttempt }: AuthFormProps) {
  const { authError } = usePortal();
  const [step, setStep] = React.useState<"enter" | "verify">("enter");
  const [prefix, setPrefix] = React.useState("+1");
  const [number, setNumber] = React.useState("");
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [resent, setResent] = React.useState(false);
  const [left, setLeft] = React.useState(0);

  const confirmationRef = React.useRef<ConfirmationResult | null>(null);
  const recaptchaRef = React.useRef<RecaptchaVerifier | null>(null);
  const recaptchaHostRef = React.useRef<HTMLDivElement | null>(null);

  // Tear down any live reCAPTCHA when the form unmounts.
  React.useEffect(
    () => () => {
      recaptchaRef.current?.clear();
      recaptchaRef.current = null;
    },
    [],
  );

  React.useEffect(() => {
    if (authError) setBusy(false);
  }, [authError]);

  // Countdown while on the verify step.
  React.useEffect(() => {
    if (step !== "verify" || left <= 0) return;
    const id = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [step, left]);

  const fullNumber = `${prefix} ${number.trim()}`;

  // A verifier is good for a single SMS, so we lazily build a fresh one for each
  // send (initial + every resend) and clear it afterwards. The host div is
  // persistent across steps, so the container is always valid.
  function ensureRecaptcha(): RecaptchaVerifier | null {
    if (!FIREBASE_ENABLED || !recaptchaHostRef.current) return null;
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(getFirebaseAuth(), recaptchaHostRef.current, {
        size: "invisible",
      });
    }
    return recaptchaRef.current;
  }

  async function requestCode(isResend: boolean) {
    setErr("");
    const digits = number.replace(/\D/g, "");
    if (digits.length < 7) {
      setErr("Enter a valid phone number.");
      return;
    }
    const verifier = ensureRecaptcha();
    if (!verifier) {
      setErr("Authentication isn’t configured yet.");
      return;
    }
    setBusy(true);
    try {
      confirmationRef.current = await signInWithPhoneNumber(
        getFirebaseAuth(),
        `${prefix}${digits}`,
        verifier,
      );
      setStep("verify");
      setCode("");
      setLeft(RESEND_SECONDS);
      setResent(isResend);
    } catch (error) {
      setErr(friendlyAuthError(error));
    } finally {
      // Consume the verifier so the next send builds a clean one.
      recaptchaRef.current?.clear();
      recaptchaRef.current = null;
      setBusy(false);
    }
  }

  function resend() {
    if (left > 0 || busy) return;
    void requestCode(true);
  }

  async function verify(submittedCode?: string) {
    const c = (submittedCode ?? code).replace(/\D/g, "");
    setErr("");
    if (c.length < 6) {
      setErr("Enter the 6-digit code we texted you.");
      return;
    }
    if (!confirmationRef.current) {
      setErr("Request a new code and try again.");
      return;
    }
    onAttempt();
    setBusy(true);
    try {
      await confirmationRef.current.confirm(c);
      // On success the portal context verifies partner access and redirects.
    } catch (error) {
      setBusy(false);
      setErr(friendlyAuthError(error));
    }
  }

  if (step === "enter") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void requestCode(false);
        }}
      >
        <div className="space-y-[7px]">
          <Label htmlFor="phone">Phone number</Label>
          <div className="flex gap-2">
            <select
              aria-label="Country code"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              className="shrink-0 rounded-[10px] border border-border bg-field px-2.5 py-[11px] font-mono text-sm text-foreground outline-none focus-visible:border-brand focus-visible:ring-[3px] focus-visible:ring-ring"
            >
              {PHONE_PREFIXES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel-national"
              placeholder="(555) 000-0000"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="flex-1"
            />
          </div>
          <p className="text-[11.5px] text-text-3">
            We’ll text you a 6-digit verification code.
          </p>
        </div>

        {err && <FormNote>{err}</FormNote>}

        <Button
          type="submit"
          size="lg"
          disabled={busy}
          className="mt-[22px] w-full"
          style={busy ? { opacity: 0.7, cursor: "wait" } : undefined}
        >
          {busy ? "Sending code…" : "Send code"}
        </Button>

        <div ref={recaptchaHostRef} />
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void verify();
      }}
    >
      <button
        type="button"
        onClick={() => {
          setStep("enter");
          setErr("");
          setCode("");
        }}
        className="mb-4 inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-semibold text-muted-foreground"
      >
        <ArrowLeft className="size-[15px]" /> Change number
      </button>

      <div className="mb-3.5 text-[13.5px] leading-[1.5] text-muted-foreground">
        Enter the code sent to{" "}
        <span className="font-mono font-semibold text-foreground">{fullNumber}</span>
      </div>

      <OTPInput value={code} onChange={setCode} onComplete={(c) => void verify(c)} />

      {err && <FormNote>{err}</FormNote>}
      {resent && !err && <FormNote tone="info">A new code is on its way.</FormNote>}

      <Button
        type="submit"
        size="lg"
        disabled={busy}
        className="mt-[22px] w-full"
        style={busy ? { opacity: 0.7, cursor: "wait" } : undefined}
      >
        {busy ? "Verifying…" : "Verify & sign in"}
      </Button>

      <div className="mt-[18px] text-center text-[13px] text-text-3">
        Didn’t get it?{" "}
        {left > 0 ? (
          <span className="text-muted-foreground">
            Resend in{" "}
            <span className="font-semibold tabular-nums">{fmtTime(left)}</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={resend}
            disabled={busy}
            className="cursor-pointer font-bold text-brand"
          >
            Resend code
          </button>
        )}
      </div>

      <div ref={recaptchaHostRef} />
    </form>
  );
}

// ── The method registry — extend here to add providers ──────────────────────
interface AuthMethod {
  id: string;
  label: string;
  icon: LucideIcon;
  Form: React.ComponentType<AuthFormProps>;
}

const AUTH_METHODS: readonly AuthMethod[] = [
  { id: "email", label: "Email", icon: Mail, Form: EmailForm },
  { id: "phone", label: "Phone", icon: Phone, Form: PhoneForm },
];

export function LoginPage() {
  const { authError, authChecking, clearAuthError } = usePortal();
  const [method, setMethod] = React.useState<string>(AUTH_METHODS[0]!.id);

  const active = AUTH_METHODS.find((m) => m.id === method) ?? AUTH_METHODS[0]!;
  const ActiveForm = active.Form;

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background text-foreground md:grid-cols-[1.05fr_.95fr]">
      {/* Left: brand panel (always the deep-space look) */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-[52px] md:flex"
        style={{
          background:
            "radial-gradient(110% 80% at 80% -10%, rgba(224,55,154,.4), transparent 55%), radial-gradient(110% 90% at -10% 110%, rgba(79,110,247,.45), transparent 60%), #0a0820",
        }}
      >
        {/* faux stars */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(1.5px 1.5px at 20% 30%, #fff, transparent), radial-gradient(1px 1px at 70% 60%, #fff, transparent), radial-gradient(1.5px 1.5px at 40% 80%, #fff, transparent), radial-gradient(1px 1px at 85% 25%, #fff, transparent), radial-gradient(1px 1px at 55% 15%, #fff, transparent)",
            backgroundSize: "320px 320px",
          }}
        />
        <div className="relative flex items-center gap-2.5">
          <div className="grid size-[34px] place-items-center rounded-[10px] bg-brand-gradient shadow-[0_6px_20px_rgba(124,58,237,.5)]">
            <Sparkles className="size-[19px] text-white" />
          </div>
          <span className="text-[15px] font-bold text-[#f1ecff]">Partner Portal</span>
        </div>
        <div className="relative">
          <div className="max-w-[440px] text-[40px] font-extrabold leading-[1.1] tracking-[-.03em] text-white">
            White-label <span className="text-brand-gradient">pop.in</span> as your own.
          </div>
          <p className="mt-4 max-w-[400px] text-[15px] leading-[1.6] text-[#b9a9e8]">
            Configure colors, logos and terminology for every brand you run — and watch it come to
            life in real time.
          </p>
          <div className="mt-7 flex gap-[22px]">
            {[
              ["Brands", "Multiple per account"],
              ["Theming", "Live preview"],
              ["Terms", "Your own currency"],
            ].map(([a, b]) => (
              <div key={a}>
                <div className="text-[13px] font-bold text-[#f1ecff]">{a}</div>
                <div className="mt-0.5 text-xs text-[#8678b5]">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-[#6a5e94]">© 2026 pop.in · Partners</div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center bg-bg-2 p-8">
        <div className="w-full max-w-[380px]">
          <PopinMark size={28} />
          <h1 className="mb-1.5 mt-[22px] text-[25px] font-extrabold tracking-[-.02em]">
            Sign in to your portal
          </h1>
          <p className="mb-[26px] text-sm text-muted-foreground">
            Manage the brands you white-label with pop.in.
          </p>

          {!FIREBASE_ENABLED && (
            <FormNote tone="info">
              Sign-in is unavailable until Firebase credentials are configured.
            </FormNote>
          )}

          {authError && <FormNote>{authError}</FormNote>}
          {authChecking && !authError && (
            <FormNote tone="info">Checking your access…</FormNote>
          )}

          <div className="mt-6">
            <MethodSwitcher methods={AUTH_METHODS} value={method} onChange={setMethod} />
            <ActiveForm onAttempt={clearAuthError} />
          </div>
        </div>
      </div>
    </div>
  );
}
